import { MyLearning } from "./MyLearning";
import { RoleOnboardingSettings } from "./RoleOnboardingSettings";
import { useState } from "react";
import type { Course } from "../../backend/api/learning";
import { useCurrentUser } from "../../backend/queries/auth";
import { useCourses } from "../../backend/queries/learning";
import {
  DataView,
  FilterBar,
  StatusBadge,
  EmptyState,
  Page,
  PageHeader,
} from "../../components/patterns";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { LearningAssignmentPanel } from "./LearningAssignmentPanel";
import { CreateCourseDialog } from "./CreateCourseDialog";
import { CoursePanel } from "./CoursePanel";

export default function Learning(): JSX.Element {
  const { data: currentUser } = useCurrentUser();
  const admin = ["Owner", "Admin"].includes(currentUser?.role || currentUser?.profile?.role || "");
  const courses = useCourses();
  const [selected, setSelected] = useState("");
  const [preview, setPreview] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [tab, setTab] = useState("library");
  const items = courses.data || [];
  const course = items.find((item) => item.id === selected);
  const filtered = items.filter(
    (item) =>
      (status === "all" || item.status === status) &&
      `${item.title} ${item.description}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  if (!admin && currentUser?.id) return <MyLearning userId={currentUser.id} />;
  return (
    <Page width="full">
      <PageHeader
        title="Learning"
        actions={admin ? <CreateCourseDialog /> : undefined}
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="library">
            {admin ? "Courses" : "My courses"}
          </TabsTrigger>
          {admin && <TabsTrigger value="assignments">Assignments</TabsTrigger>}
          {admin && <TabsTrigger value="onboarding">Onboarding</TabsTrigger>}
        </TabsList>
        <TabsContent value="library" className="mt-5 space-y-4">
          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchLabel="Search courses"
            searchPlaceholder="Search courses…"
            summary={`${filtered.length} ${filtered.length === 1 ? "course" : "courses"}`}
            onClear={
              search || status !== "all"
                ? () => {
                    setSearch("");
                    setStatus("all");
                  }
                : undefined
            }
          >
            {admin && (
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger
                  className="w-full sm:w-40"
                  aria-label="Course status"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            )}
          </FilterBar>
          <DataView<Course>
            caption={admin ? "Course library" : "Assigned courses"}
            rows={filtered}
            getRowKey={(item) => item.id}
            status={
              courses.isPending ? "loading" : courses.error ? "error" : "ready"
            }
            onRetry={() => courses.refetch()}
            columns={[
              {
                id: "title",
                header: "Course",
                primary: true,
                cell: (item) => (
                  <span className="font-medium">{item.title}</span>
                ),
              },
              {
                id: "status",
                header: "Status",
                cell: (item) => (
                  <StatusBadge
                    tone={item.status === "published" ? "success" : "neutral"}
                  >
                    {item.status === "published"
                      ? "Published"
                      : item.status === "draft"
                        ? "Draft"
                        : item.status}
                  </StatusBadge>
                ),
              },
            ]}
            rowActions={(item) => (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelected(item.id)}
              >
                {admin ? "Manage" : "Open"}
              </Button>
            )}
            empty={
              <EmptyState
                size="inline"
                title={
                  items.length
                    ? "No matching courses"
                    : admin
                      ? "No courses yet"
                      : "No courses assigned yet"
                }
                description={
                  items.length
                    ? "Try another search or clear the filters."
                    : admin
                      ? "Add a course, then publish and assign it."
                      : "Your administrator will assign your onboarding and training here."
                }
                actions={
                  items.length ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearch("");
                        setStatus("all");
                      }}
                    >
                      Clear filters
                    </Button>
                  ) : admin ? (
                    <CreateCourseDialog />
                  ) : undefined
                }
              />
            }
          />
        </TabsContent>
        {admin && (
          <TabsContent value="assignments" className="mt-5">
            <LearningAssignmentPanel />
          </TabsContent>
        )}
        {admin && (
          <TabsContent value="onboarding" className="mt-5">
            <RoleOnboardingSettings />
          </TabsContent>
        )}
      </Tabs>
      <Dialog
        open={Boolean(course)}
        onOpenChange={(open) => {
          if (!open) {
            setSelected("");
            setPreview(false);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{admin ? "Manage course" : "Your course"}</DialogTitle>
            <DialogDescription>
              {admin
                ? "Review content, publish drafts, and assign learning."
                : "Complete the activity below to finish your training."}
            </DialogDescription>
          </DialogHeader>
          {course && (
            <>
              <Button
                variant="ghost"
                className="w-fit"
                onClick={() => setPreview((value) => !value)}
              >
                {preview ? "Back to editing" : "Preview as learner"}
              </Button>
              {preview && (
                <p className="text-xs text-muted-foreground">
                  Learner preview · completion is disabled here.
                </p>
              )}
              <CoursePanel
                key={`${course.id}-${preview}`}
                course={course}
                admin={!preview}
                preview={preview}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
}
